import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import KYCReviewLayout from './KYCTab/KYCReviewLayout';
import KYCDocumentChecklist from './KYCDocumentChecklist';
import KYCVersionSelector from './KYCTab/KYCVersionSelector';
import KYCVersionDiffView from './KYCTab/KYCVersionDiffView';
import kycService from '../../../services/kycService';
import { toast } from 'sonner';
import { logger } from '../../../utils/logger';
import type { KYCProfileVersion, VersionDiff } from '../../../types/kyc.types';
import { listStagedProposals } from '../../../services/kycProposalService';
// TASK-210: Import seed KYC data
import { getClientKYC } from '../../../services/mock/comprehensiveClientSeedData';

interface KYCTabProps {
  clientId: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  onNavigateToDocuments?: () => void; // TASK-209: Navigate to Documents tab
}

interface KYCDisplayData {
  status: 'complete' | 'incomplete' | 'in_progress' | 'approved';
  completion_percentage: number;
  last_updated: string;
  sections: Record<string, any>;
  backendStatus?: string;
}

const KYCTab: React.FC<KYCTabProps> = ({ clientId, onNavigateToDocuments }) => {
  const navigate = useNavigate();
  const [kycData, setKYCData] = useState<KYCDisplayData | null>(null);
  const [loading, setLoading] = useState(true);

  // TASK-202: Versioning state
  const [selectedVersion, setSelectedVersion] = useState<KYCProfileVersion | null>(null);
  const [showDiff, setShowDiff] = useState(false);
  const [diffData, setDiffData] = useState<VersionDiff | null>(null);
  const [loadingDiff, setLoadingDiff] = useState(false);

  // TASK-618: pending staged-proposal count (indicator only, not a destination)
  const [pendingProposals, setPendingProposals] = useState(0);

  useEffect(() => {
    fetchKYCData();
    listStagedProposals(clientId)
      .then((res) => setPendingProposals(res.proposals.length))
      .catch(() => setPendingProposals(0));
  }, [clientId]);

  const fetchKYCData = async () => {
    setLoading(true);
    try {
      let profile = null;

      // TASK-210: Try API first, fallback to seed data
      try {
        profile = await kycService.profiles.get(clientId);
      } catch (apiError: any) {
        logger.debug('API KYC fetch failed, trying seed data:', apiError);
      }
      // TASK-652: absence now returns null (200 not_created, no 404) — the seed
      // fallback applies to both the null and the error path.
      if (!profile) {
        const seedKYC = getClientKYC(clientId);
        if (seedKYC) {
          // Seed KYC data is already in the correct API format
          profile = seedKYC;
          logger.debug('Loaded KYC from seed data:', clientId);
        }
      }

      if (profile) {
        // Transform backend data to display format
        const displayData = transformKYCProfile(profile);
        setKYCData(displayData);
      } else {
        setKYCData(null);
      }
    } catch (error: any) {
      logger.error('Error fetching KYC data:', error);
      // If it's a 404, the profile doesn't exist yet
      if (error.response?.status === 404) {
        setKYCData(null);
      } else {
        toast.error('Failed to load KYC data');
      }
    } finally {
      setLoading(false);
    }
  };

  // TASK-202: Handle version selection
  const handleVersionChange = async (version: KYCProfileVersion) => {
    setSelectedVersion(version);
    setShowDiff(false);
    setDiffData(null);

    // If selecting a historical version, load the snapshot and diff
    if (!version.isCurrent) {
      setLoadingDiff(true);
      try {
        // Load version snapshot
        const snapshot = await kycService.getVersionSnapshot(version.id);
        // Transform snapshot to display format
        const displayData = transformKYCProfile(snapshot.snapshot);
        setKYCData(displayData);

        // Load diff automatically for historical versions
        const diffResponse = await kycService.getVersionDiff(version.id);
        setDiffData(diffResponse.diff);
        setShowDiff(true);
      } catch (error: any) {
        logger.error('Error loading version:', error);
        toast.error('Failed to load version snapshot');
      } finally {
        setLoadingDiff(false);
      }
    } else {
      // Reload current version
      fetchKYCData();
    }
  };

  const transformKYCProfile = (profile: any): KYCDisplayData => {
    // Calculate completion for each section
    const personalInfoComplete = !!(profile.first_name && profile.last_name && profile.date_of_birth);
    const contactInfoComplete = !!(profile.phone_primary && profile.email_primary);
    const employmentInfoComplete = !!(profile.employment_status);
    const taxInfoComplete = !!(profile.tax_residence);
    const personalBackgroundComplete = !!(profile.personal_background);
    const pepStatusComplete = !!(profile.pep_status);
    const adverseMediaComplete = !!(profile.adverse_media && profile.adverse_media.length > 0);
    const businessActivitiesComplete = !!(profile.business_activities && profile.business_activities.length > 0);
    const wealthSourceComplete = !!(profile.wealth_sources && profile.wealth_sources.length > 0);
    const incomeSourceComplete = !!(profile.income_sources && profile.income_sources.length > 0);

    const sections = [
      personalInfoComplete,
      contactInfoComplete,
      employmentInfoComplete,
      taxInfoComplete,
      personalBackgroundComplete,
      pepStatusComplete || adverseMediaComplete,
      businessActivitiesComplete,
      wealthSourceComplete || incomeSourceComplete,
    ];

    const completedSections = sections.filter(Boolean).length;
    const totalSections = 8; // We're displaying 8 main sections
    const completionPercentage = Math.round((completedSections / totalSections) * 100);

    // Use backend status when available (approved/submitted take priority)
    const backendStatus = profile.status as string | undefined;
    let status: 'complete' | 'incomplete' | 'in_progress' | 'approved';
    if (backendStatus === 'approved') {
      status = 'approved';
    } else if (completionPercentage === 0) {
      status = 'incomplete';
    } else if (completionPercentage === 100) {
      status = 'complete';
    } else {
      status = 'in_progress';
    }

    return {
      status,
      completion_percentage: completionPercentage,
      last_updated: profile.updated_at || profile.created_at || new Date().toISOString(),
      backendStatus,
      sections: {
        personalInfo: {
          complete: personalInfoComplete,
          firstName: profile.first_name,
          middleName: profile.middle_names,
          lastName: profile.last_name,
          dateOfBirth: profile.date_of_birth,
          placeOfBirth: profile.place_of_birth,
          gender: profile.gender,
          maritalStatus: profile.marital_status,
          nationalities: profile.nationalities || [],
          passportNumber: profile.passport_number,
          nationalId: profile.national_id,
          ssn: profile.ssn,
          formerNames: profile.former_names || [],
          languages: profile.languages || [],
          missing_fields: getPersonalInfoMissingFields(profile),
        },
        contactInfo: {
          complete: contactInfoComplete,
          phonePrimary: profile.phone_primary,
          phoneSecondary: profile.phone_secondary,
          emailPrimary: profile.email_primary,
          emailSecondary: profile.email_secondary,
          residentialAddress: formatAddress(profile.residential_address),
          mailingAddress: formatAddress(profile.mailing_address),
          preferredContactMethod: profile.preferred_contact_method,
          missing_fields: getContactInfoMissingFields(profile),
        },
        employmentInfo: {
          complete: employmentInfoComplete,
          employerName: profile.employer_name,
          occupation: profile.occupation,
          employmentStatus: profile.employment_status,
          employmentIndustry: profile.employment_industry,
          yearsEmployed: profile.years_employed,
          annualIncome: profile.annual_income,
          incomeCurrency: profile.income_currency,
          missing_fields: getEmploymentInfoMissingFields(profile),
        },
        taxInfo: {
          complete: taxInfoComplete,
          taxResidence: profile.tax_residence,
          usPerson: profile.us_person,
          taxId: profile.tax_id,
          fatcaStatus: profile.fatca_status,
          missing_fields: getTaxInfoMissingFields(profile),
        },
        personalBackground: {
          complete: personalBackgroundComplete,
          educationLevel: profile.personal_background?.education_level,
          familyComposition: profile.personal_background?.family_composition,
          dependentsCount: profile.personal_background?.dependents_count,
          institutionsAttended: profile.personal_background?.institutions_attended || [],
          professionalCertifications: profile.personal_background?.professional_certifications || [],
          hobbiesInterests: profile.personal_background?.hobbies_interests || [],
          spousePartnerEmploymentStatus: profile.personal_background?.spouse_partner_employment_status,
          missing_fields: !personalBackgroundComplete ? ['Personal background information required'] : [],
        },
        // PEP Status as separate non-array section (matches KYC_FIELD_MAPPING id: 'pepStatus')
        pepStatus: {
          isPEP: profile.pep_status?.is_pep || false,
          pepType: profile.pep_status?.pep_type,
          position: profile.pep_status?.position || (profile.pep_status?.positions || [])[0],
        },
        // Adverse media as array section (matches KYC_FIELD_MAPPING id: 'adverseMedia', isArray: true)
        adverseMedia: (profile.adverse_media || []).map((am: any) => ({
          hasAdverseMedia: am.has_adverse_media ?? am.hasAdverseMedia ?? true,
          severityLevel: am.severity_level || am.severityLevel,
          matchScore: am.match_score || am.matchScore,
          riskImpact: am.risk_impact || am.riskImpact,
          reviewed: am.reviewed,
        })),
        // Array sections — plain arrays with camelCase keys matching KYC_FIELD_MAPPING extractionPaths
        wealthSources: (profile.wealth_sources || []).map((ws: any) => ({
          sourceType: ws.source_type || ws.sourceType,
          sourceDescription: ws.source_description || ws.sourceDescription,
          assetValue: ws.asset_value || ws.assetValue,
          assetCurrency: ws.asset_currency || ws.assetCurrency,
          percentage: ws.percentage,
        })),
        incomeSources: (profile.income_sources || []).map((src: any) => ({
          incomeType: src.income_type || src.incomeType,
          incomeDescription: src.income_description || src.incomeDescription || src.source_name || src.sourceName,
          annualAmount: src.annual_amount || src.annualAmount,
          currency: src.currency,
        })),
        businessActivities: (profile.business_activities || []).map((ba: any) => ({
          businessName: ba.business_name || ba.businessName,
          businessType: ba.business_type || ba.businessType,
          primaryIndustry: ba.primary_industry || ba.primaryIndustry,
          ownershipPercentage: ba.ownership_percentage || ba.ownershipPercentage,
        })),
      },
    };
  };

  const formatAddress = (address: any): string => {
    if (!address) return 'Not provided';
    const parts = [
      address.street_address,
      address.city,
      address.state_province,
      address.postal_code,
      address.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Not provided';
  };

  const getPersonalInfoMissingFields = (profile: any): string[] => {
    const missing = [];
    if (!profile.first_name) missing.push('First Name');
    if (!profile.last_name) missing.push('Last Name');
    if (!profile.date_of_birth) missing.push('Date of Birth');
    if (!profile.nationalities || profile.nationalities.length === 0) missing.push('Nationalities');
    return missing;
  };

  const getContactInfoMissingFields = (profile: any): string[] => {
    const missing = [];
    if (!profile.phone_primary) missing.push('Primary Phone');
    if (!profile.email_primary) missing.push('Primary Email');
    if (!profile.residential_address) missing.push('Residential Address');
    return missing;
  };

  const getEmploymentInfoMissingFields = (profile: any): string[] => {
    const missing = [];
    if (!profile.employment_status) missing.push('Employment Status');
    return missing;
  };

  const getTaxInfoMissingFields = (profile: any): string[] => {
    const missing = [];
    if (!profile.tax_residence) missing.push('Tax Residence');
    if (profile.us_person === null || profile.us_person === undefined) missing.push('US Person Status');
    return missing;
  };

  const handleDownloadPDF = async () => {
    try {
      await kycService.downloadKYCPDF(clientId);
      toast.success('PDF downloaded successfully');
    } catch (error: any) {
      logger.error('Failed to download PDF:', error);
      toast.error(error.message || 'Failed to download PDF');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-6 animate-pulse">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-6 h-6 bg-gray-200 rounded" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 bg-gray-200 rounded w-2/3" />
                  <div className="h-3 bg-gray-200 rounded w-1/3" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state - No KYC data
  if (!kycData) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">No KYC data available</p>
        <p className="text-sm text-gray-500 mt-1 mb-4">
          Start the KYC process for this client
        </p>
        <button
          onClick={() => navigate(`/clients/${clientId}/kyc-workspace`)}
          className="px-4 py-2 bg-kielis-gold text-kielis-navy rounded-md hover:bg-opacity-90 font-medium"
        >
          Complete KYC
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* TASK-618: pending staged proposals indicator */}
      {pendingProposals > 0 && (
        <div
          data-testid="kyc-pending-proposals-badge"
          className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
        >
          <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs font-semibold text-white bg-amber-500 rounded-full">
            {pendingProposals}
          </span>
          proposed profile update{pendingProposals !== 1 ? 's' : ''} pending review in the KYC workspace
        </div>
      )}

      {/* TASK-202: Version History Selector */}
      <KYCVersionSelector
        clientId={clientId}
        currentVersion={selectedVersion}
        onVersionSelect={handleVersionChange}
      />

      {/* TASK-202: Show/Hide Changes Toggle */}
      {selectedVersion && !selectedVersion.isCurrent && diffData && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowDiff(!showDiff)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2 px-3 py-2 rounded-md hover:bg-blue-50 transition-colors"
          >
            {showDiff ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showDiff ? 'Hide' : 'Show'} Changes from Previous Version
          </button>
        </div>
      )}

      {/* TASK-202: Version Diff View */}
      {showDiff && diffData && !loadingDiff && (
        <KYCVersionDiffView
          diff={diffData}
          versionNumber={selectedVersion?.versionNumber}
        />
      )}

      {/* TASK-202: Loading State */}
      {loadingDiff && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-center gap-3">
            <div className="w-5 h-5 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-blue-700 font-medium">Loading version snapshot and changes...</span>
          </div>
        </div>
      )}

      {/* NEW: KYC Review Layout with sidebar navigation (Story 2.2.9) */}
      <KYCReviewLayout
        kycData={kycData}
        clientId={clientId}
        onEdit={() => navigate(`/clients/${clientId}/kyc-workspace`)}
        onDownloadPDF={handleDownloadPDF}
      />

      {/* Document Checklist - TASK-209 */}
      <KYCDocumentChecklist
        clientId={clientId}
        onNavigateToDocuments={onNavigateToDocuments}
      />
    </div>
  );
};

export default KYCTab;
